// Debt payoff math — educational illustrations only, not financial advice.

const MAX_MONTHS = 1200; // 100-year safety cap

export const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(amount || 0);

// Estimate a minimum payment when the user hasn't entered one.
export const estimateMin = (balance, rate) => {
  const monthlyInterest = balance * (rate / 100 / 12);
  return Math.max(balance * 0.02, 25, monthlyInterest * 1.05);
};

// Map a liability row to the debt shape this engine needs.
export const liabilityToDebt = (l) => {
  const balance = parseFloat(l.value) || 0;
  const rate = l.interest_rate != null && l.interest_rate !== '' ? parseFloat(l.interest_rate) : 0;
  const enteredMin = l.monthly_payment != null && l.monthly_payment !== '' ? parseFloat(l.monthly_payment) : 0;
  const minPayment = enteredMin > 0 ? enteredMin : estimateMin(balance, rate);
  return {
    id: l.id,
    name: l.name,
    type: l.category || 'other',
    balance,
    rate,
    minPayment,
    estimatedMin: !(enteredMin > 0),
  };
};

const orderDebts = (list, strategy, customOrder = []) => {
  const byId = Object.fromEntries(list.map((d) => [d.id, d]));
  switch (strategy) {
    case 'snowball':
      return [...list].sort((a, b) => a.balance - b.balance);
    case 'cashflow':
      return [...list].sort((a, b) => b.minPayment - a.minPayment);
    case 'custom':
      return customOrder.length
        ? customOrder.map((id) => byId[id]).filter(Boolean).concat(list.filter((d) => !customOrder.includes(d.id)))
        : [...list];
    case 'avalanche':
    default:
      return [...list].sort((a, b) => b.rate - a.rate);
  }
};

export const weightedAvgRate = (debts) => {
  const total = debts.reduce((s, d) => s + d.balance, 0);
  if (total <= 0) return 0;
  return debts.reduce((s, d) => s + d.balance * d.rate, 0) / total;
};

export const monthsToDate = (months) => {
  if (!isFinite(months)) return null;
  const d = new Date();
  d.setMonth(d.getMonth() + Math.round(months));
  return d;
};

export const formatMonthYear = (date) =>
  date ? date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '—';

// Core simulation: constant rollover budget (sum of minimums + extra), held steady
// so freed minimums "snowball" into the targeted debt.
export const simulate = (debts, { strategy = 'avalanche', extra = 0, customOrder = [] } = {}) => {
  const work = debts.map((d) => ({ ...d, interestPaid: 0, paidMonth: null }));
  const baseMin = work.reduce((s, d) => s + d.minPayment, 0);
  const budget = baseMin + (extra || 0);
  const startingTotal = work.reduce((s, d) => s + d.balance, 0);
  const schedule = [{ month: 0, totalBalance: startingTotal, interestToDate: 0 }];
  const events = [];
  let month = 0;
  let totalInterest = 0;

  while (work.some((d) => d.balance > 0.005) && month < MAX_MONTHS) {
    month++;
    // Accrue interest
    for (const d of work) {
      if (d.balance > 0) {
        const i = d.balance * (d.rate / 100 / 12);
        d.balance += i;
        d.interestPaid += i;
        totalInterest += i;
      }
    }
    let avail = budget;
    // Pay minimums on every active debt
    for (const d of work) {
      if (d.balance > 0 && avail > 0) {
        const pay = Math.min(d.minPayment, d.balance, avail);
        d.balance -= pay;
        avail -= pay;
      }
    }
    // Throw everything left at the targeted debt(s), in strategy order
    for (const d of orderDebts(work, strategy, customOrder)) {
      if (avail <= 0) break;
      if (d.balance > 0) {
        const pay = Math.min(avail, d.balance);
        d.balance -= pay;
        avail -= pay;
      }
    }
    // Record payoffs this month
    for (const d of work) {
      if (d.balance <= 0.005 && d.paidMonth == null) {
        d.balance = 0;
        d.paidMonth = month;
        events.push({ month, id: d.id, name: d.name, freed: d.minPayment });
      }
    }
    schedule.push({
      month,
      totalBalance: work.reduce((s, d) => s + Math.max(0, d.balance), 0),
      interestToDate: totalInterest,
    });
  }

  const solved = work.every((d) => d.balance <= 0.005);

  // Cumulative "payment power" for the waterfall
  let power = 0;
  const waterfall = [...events]
    .sort((a, b) => a.month - b.month)
    .map((e) => { power += e.freed; return { ...e, power, date: monthsToDate(e.month) }; });

  const perDebt = {};
  work.forEach((d) => {
    perDebt[d.id] = { name: d.name, paidMonth: d.paidMonth, interestPaid: d.interestPaid, date: monthsToDate(d.paidMonth) };
  });

  return {
    solved,
    months: solved ? month : Infinity,
    payoffDate: solved ? monthsToDate(month) : null,
    totalInterest,
    startingTotal,
    baseMin,
    budget,
    schedule,
    events: waterfall,
    perDebt,
    order: orderDebts(debts, strategy, customOrder).map((d) => d.id),
  };
};

const STRATEGY_LABELS = {
  snowball: 'Snowball',
  avalanche: 'Avalanche',
  cashflow: 'Cash Flow',
  custom: 'Custom',
};

export const strategyMeta = {
  snowball: { label: 'Snowball', blurb: 'Pay smallest balance first — quick wins and momentum.' },
  avalanche: { label: 'Avalanche', blurb: 'Pay highest interest rate first — minimizes total interest.' },
  cashflow: { label: 'Cash Flow', blurb: 'Pay the largest minimum first — frees up monthly cash flow.' },
  custom: { label: 'Custom', blurb: 'Set your own payoff priority order.' },
};

// Side-by-side comparison of the three rule-based methods.
export const compareStrategies = (debts, extra = 0) => {
  const results = ['snowball', 'avalanche', 'cashflow'].map((strategy) => {
    const r = simulate(debts, { strategy, extra });
    return {
      strategy,
      label: STRATEGY_LABELS[strategy],
      months: r.months,
      payoffDate: r.payoffDate,
      totalInterest: r.totalInterest,
      solved: r.solved,
    };
  });
  const solved = results.filter((r) => r.solved);
  const maxInterest = solved.length ? Math.max(...solved.map((r) => r.totalInterest)) : 0;
  results.forEach((r) => { r.interestSaved = r.solved ? maxInterest - r.totalInterest : 0; });
  const recommended = solved.length
    ? solved.reduce((best, r) => (r.totalInterest < best.totalInterest ? r : best)).strategy
    : null;
  return { results, recommended, maxInterest };
};
